import * as React from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import { Upload } from "lucide-react";
import { createBatch } from "../../api/batches";
import { usePortalScope } from "@/analytics/PortalScope";
import { formatBytes, sanityCheck } from "./files";
import { buildBatchParams, explicitParamsValid } from "./batchParams";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (batchId: string) => void;
}

type Mode = "auto" | "explicit";
type JobType = "invoice_audit" | "run_skill";
type ContractMode = "none" | "shared" | "per_invoice";

export function NewBatchDialog({ open, onClose, onCreated }: Props) {
  const [files, setFiles] = React.useState<File[]>([]);
  const [mode, setMode] = React.useState<Mode>("auto");
  const [type, setType] = React.useState<JobType>("invoice_audit");
  const [contractMode, setContractMode] = React.useState<ContractMode>("per_invoice");
  const [sharedContract, setSharedContract] = React.useState("");
  const [skillName, setSkillName] = React.useState("");
  const [prompt, setPrompt] = React.useState("");
  const [name, setName] = React.useState("");
  const [dragging, setDragging] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // The dialog and each Select's menu are portaled out of the page (D7).
  const paperScope = usePortalScope("create_dialog");
  const typeMenuScope = usePortalScope("create_dialog-type");
  const contractMenuScope = usePortalScope("create_dialog-contract");
  const sharedMenuScope = usePortalScope("create_dialog-shared_contract");

  const sanity = sanityCheck(files);
  const loose = files.filter((f) => !f.name.toLowerCase().endsWith(".zip"));

  function addFiles(list: FileList | File[] | null) {
    if (!list) return;
    const incoming = Array.from(list);
    setFiles((cur) => {
      const seen = new Set(cur.map((f) => `${f.name}:${f.size}`));
      return [...cur, ...incoming.filter((f) => !seen.has(`${f.name}:${f.size}`))];
    });
  }

  const paramsInput = { mode, type, skillName, prompt, contractMode, sharedContract };
  const explicitInvalid = !explicitParamsValid(paramsInput);
  const canSubmit = files.length > 0 && !submitting && !explicitInvalid && sanity.warnings.length === 0;

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const params = buildBatchParams(paramsInput);
      const res = await createBatch({
        files,
        mode,
        type: mode === "explicit" ? type : undefined,
        params: Object.keys(params).length ? params : undefined,
        name: name.trim() || undefined,
      });
      setFiles([]);
      setName("");
      onCreated(res.batch.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth slotProps={{ paper: paperScope }}>
      <DialogTitle>New batch</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box
            data-testid="batch-dropzone"
            data-ph="upload"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            sx={{
              border: "2px dashed", borderColor: dragging ? "primary.main" : "divider",
              borderRadius: 2, p: 3, textAlign: "center", cursor: "pointer",
            }}
          >
            <Upload size={22} />
            <Typography variant="body2">
              Drop files or a .zip here, or click to browse
            </Typography>
            <input
              ref={inputRef}
              type="file"
              multiple
              hidden
              data-testid="batch-file-input"
              accept=".pdf,.zip,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            />
          </Box>

          {files.length > 0 && (
            <Typography variant="body2" data-testid="batch-file-summary">
              {sanity.count} file{sanity.count === 1 ? "" : "s"} · {formatBytes(sanity.bytes)}
              {" "}
              <Button size="small" data-ph="delete_files" onClick={() => setFiles([])}>Clear</Button>
            </Typography>
          )}
          {sanity.warnings.map((w) => (
            <Alert key={w} severity="warning">{w}</Alert>
          ))}

          <ToggleButtonGroup
            data-ph-scope="mode"
            exclusive
            size="small"
            value={mode}
            onChange={(_, v: Mode | null) => v && setMode(v)}
            aria-label="mode"
          >
            <ToggleButton value="auto" data-testid="mode-auto" data-ph="select_auto">Auto</ToggleButton>
            <ToggleButton value="explicit" data-testid="mode-explicit" data-ph="select_explicit">Explicit</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {mode === "auto"
              ? "Artie classifies each file, pairs invoices with contracts, and holds anything it is unsure about for your review."
              : "You choose the job type; every file runs it."}
          </Typography>

          {mode === "explicit" && (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel id="batch-type-label">Job type</InputLabel>
                <Select
                  labelId="batch-type-label"
                  label="Job type"
                  value={type}
                  onChange={(e) => setType(e.target.value as JobType)}
                  data-testid="batch-type-select"
                  data-ph="type-open"
                  MenuProps={{ slotProps: { list: typeMenuScope } }}
                >
                  <MenuItem value="invoice_audit" data-ph="select">Invoice audit</MenuItem>
                  <MenuItem value="run_skill" data-ph="select">Run skill</MenuItem>
                </Select>
              </FormControl>

              {type === "invoice_audit" && (
                <>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="batch-contract-label">Contract</InputLabel>
                    <Select
                      labelId="batch-contract-label"
                      label="Contract"
                      value={contractMode}
                      onChange={(e) => setContractMode(e.target.value as ContractMode)}
                      data-testid="batch-contract-mode"
                      data-ph="contract-open"
                      MenuProps={{ slotProps: { list: contractMenuScope } }}
                    >
                      <MenuItem value="none" data-ph="select">No contract</MenuItem>
                      <MenuItem value="shared" data-ph="select">One shared contract</MenuItem>
                      <MenuItem value="per_invoice" data-ph="select">Pair per invoice</MenuItem>
                    </Select>
                  </FormControl>
                  {contractMode === "shared" && (
                    <FormControl size="small" fullWidth>
                      <InputLabel id="batch-shared-label">Shared contract file</InputLabel>
                      <Select
                        labelId="batch-shared-label"
                        label="Shared contract file"
                        value={sharedContract}
                        onChange={(e) => setSharedContract(e.target.value)}
                        data-testid="batch-shared-contract"
                        data-ph="shared_contract-open"
                        MenuProps={{ slotProps: { list: sharedMenuScope } }}
                      >
                        {loose.map((f) => (
                          <MenuItem key={f.name} value={f.name} data-ph="select">{f.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}

              {type === "run_skill" && (
                <>
                  <TextField
                    size="small" label="Skill name" value={skillName}
                    onChange={(e) => setSkillName(e.target.value)}
                    slotProps={{ htmlInput: { "data-testid": "batch-skill-name" } }}
                  />
                  <TextField
                    size="small" label="Prompt" multiline minRows={3} value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    slotProps={{ htmlInput: { "data-testid": "batch-skill-prompt" } }}
                  />
                </>
              )}
            </>
          )}

          <TextField
            size="small" label="Batch name (optional)" value={name}
            onChange={(e) => setName(e.target.value)}
            slotProps={{ htmlInput: { "data-testid": "batch-name" } }}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} data-ph="cancel">Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canSubmit}
          data-testid="create-batch-button"
          data-ph="submit"
          startIcon={submitting ? <CircularProgress size={14} /> : undefined}
        >
          {submitting ? "Uploading…" : "Create batch"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
